import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShoppingCart, CreditCard, CheckCircle2, TrendingDown, AlertTriangle, UserX, Mail } from "lucide-react";
import { adminApi } from "../api";

const NUM = (v) => (Number(v) || 0).toLocaleString("pt-PT");
const MONTHS = ["Ano inteiro", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function Kpi({ label, value, icon: Icon, accent }) {
  const cls = { info: "bg-blue-50 border-blue-200 text-blue-900", warn: "bg-amber-50 border-amber-200 text-amber-900", ok: "bg-emerald-50 border-emerald-200 text-emerald-900", def: "bg-white border-[#F3F0E7] text-[#1B1A17]" }[accent || "def"];
  return (
    <div className={`p-4 rounded-lg border ${cls}`}>
      <div className="flex items-center gap-2 opacity-70"><Icon size={14} /><p className="text-[10px] uppercase tracking-[0.15em] font-semibold">{label}</p></div>
      <p className="text-2xl font-bold font-mono mt-1">{value}</p>
    </div>
  );
}

export default function AdminCheckoutFunnel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminApi.marketingCheckoutFunnel(year, month)); }
    catch (e) { toast.error(e.message || "Erro a carregar o funil"); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const f = data?.funil || {};
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  const [cleaning, setCleaning] = useState(false);
  const cleanFalseBlocks = useCallback(async () => {
    try {
      setCleaning(true);
      const dry = await adminApi.marketingCheckoutFunnelCleanup({});
      const n = dry?.matched || 0;
      if (!n) { toast.info("Não há bloqueios antigos de 'morada de envio' para limpar."); return; }
      if (!window.confirm(
        `Vão ser apagados ${n} eventos antigos de bloqueio "sem morada de envio" (falsos positivos, anteriores a agora). ` +
        `Bloqueios futuros legítimos são preservados. Continuar?`
      )) return;
      const res = await adminApi.marketingCheckoutFunnelCleanup({ confirm: true });
      toast.success(`${res?.deleted || 0} bloqueios falsos removidos.`);
      await load();
    } catch (e) {
      toast.error(e.message || "Erro a limpar bloqueios");
    } finally {
      setCleaning(false);
    }
  }, [load]);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-checkout-funnel-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[#978D78] hover:text-[#0F1829] mb-4"><ArrowLeft size={15} /> Voltar</Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1829] flex items-center gap-2"><ShoppingCart size={22} /> Funil de Checkout</h1>
          <p className="text-sm text-[#978D78] mt-1">Quantos chegam ao checkout, quantos concluem, e os erros/bloqueios que não ultrapassam.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="funnel-year" className="px-3 py-2 text-sm border border-[#E6E1D6] rounded bg-white">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="funnel-month" className="px-3 py-2 text-sm border border-[#E6E1D6] rounded bg-white">
            {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#978D78]"><Loader2 size={22} className="animate-spin mr-2" /> A carregar…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Kpi label="Chegaram ao checkout" value={NUM(f.checkout)} icon={ShoppingCart} accent="info" />
            <Kpi label="Chegaram ao pagamento" value={NUM(f.pagamento)} icon={CreditCard} />
            <Kpi label="Concluíram a compra" value={NUM(f.concluido)} icon={CheckCircle2} accent="ok" />
            <Kpi label="Taxa de abandono" value={`${f.abandono_pct ?? 0}%`} icon={TrendingDown} accent="warn" />
          </div>

          <div className="bg-white border border-[#F3F0E7] rounded-lg p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-1">
              <h2 className="text-lg font-semibold text-[#0F1829] flex items-center gap-2"><AlertTriangle size={18} /> Bloqueios e erros que não ultrapassaram</h2>
              <button
                onClick={cleanFalseBlocks}
                disabled={cleaning}
                data-testid="funnel-clean-false-blocks-btn"
                className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#F3F0E7] text-[#978D78] hover:text-[#0F1829] hover:border-[#E4DFD2] disabled:opacity-50"
                title="Remove os bloqueios antigos de 'morada de envio' que eram falsos positivos (cursos/eventos)"
              >
                {cleaning ? <Loader2 size={13} className="animate-spin" /> : <AlertTriangle size={13} />}
                Limpar falsos "morada de envio"
              </button>
            </div>
            <p className="text-xs text-[#978D78] mb-4">Nº de sessões distintas que ficaram presas em cada mensagem.</p>
            {(data?.bloqueios || []).length === 0 ? (
              <p className="text-sm text-[#978D78] py-6 text-center" data-testid="funnel-blocks-empty">Sem bloqueios/erros registados neste período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="funnel-blocks-table">
                  <thead><tr className="text-left text-[#978D78] border-b border-[#F3F0E7]">
                    <th className="py-2 pr-3 font-semibold w-24">Tipo</th>
                    <th className="py-2 pr-3 font-semibold">Mensagem</th>
                    <th className="py-2 font-semibold text-right w-24">Sessões</th>
                  </tr></thead>
                  <tbody>
                    {data.bloqueios.map((b, i) => (
                      <tr key={i} className="border-b border-[#F8F6F0] hover:bg-[#FBFAF6]" data-testid={`funnel-block-row-${i}`}>
                        <td className="py-2 pr-3"><span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${b.tipo === "Erro" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{b.tipo}</span></td>
                        <td className="py-2 pr-3 text-[#1B1A17]">{b.mensagem}</td>
                        <td className="py-2 text-right font-mono font-semibold">{NUM(b.sessoes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#F3F0E7] rounded-lg p-4 sm:p-5 mt-6">
            <h2 className="text-lg font-semibold text-[#0F1829] mb-1 flex items-center gap-2"><UserX size={18} /> Carrinhos não concluídos</h2>
            <p className="text-xs text-[#978D78] mb-4">Clientes que chegaram ao checkout e não finalizaram — nome, email e conteúdo do carrinho. (máx. 200 sessões recentes)</p>
            {(data?.abandonos || []).length === 0 ? (
              <p className="text-sm text-[#978D78] py-6 text-center" data-testid="funnel-abandoned-empty">Sem carrinhos não concluídos neste período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="funnel-abandoned-table">
                  <thead><tr className="text-left text-[#978D78] border-b border-[#F3F0E7]">
                    <th className="py-2 pr-3 font-semibold">Cliente</th>
                    <th className="py-2 pr-3 font-semibold">Email</th>
                    <th className="py-2 pr-3 font-semibold">Conteúdo do carrinho</th>
                    <th className="py-2 pr-3 font-semibold text-right w-24">Total</th>
                    <th className="py-2 pr-3 font-semibold w-28">Último passo</th>
                    <th className="py-2 font-semibold w-32">Data</th>
                  </tr></thead>
                  <tbody>
                    {data.abandonos.map((a, i) => (
                      <tr key={a.session_id || i} className="border-b border-[#F8F6F0] hover:bg-[#FBFAF6] align-top" data-testid={`funnel-abandoned-row-${i}`}>
                        <td className="py-2 pr-3 text-[#1B1A17] font-medium">{a.nome || (a.ID_Cliente ? `Cliente #${a.ID_Cliente}` : "Anónimo")}</td>
                        <td className="py-2 pr-3">
                          {a.email ? (
                            <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 text-blue-700 hover:underline"><Mail size={12} /> {a.email}</a>
                          ) : <span className="text-[#B8B0A0]">—</span>}
                        </td>
                        <td className="py-2 pr-3 text-[#1B1A17]">
                          <ul className="space-y-0.5">
                            {(a.cart || []).map((it, j) => (
                              <li key={j} className="flex justify-between gap-2">
                                <span>{it.qtd}× {it.nome}</span>
                                <span className="font-mono text-[#978D78] whitespace-nowrap">{(Number(it.preco) || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" })}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                        <td className="py-2 pr-3 text-right font-mono font-semibold whitespace-nowrap">{(Number(a.total) || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" })}</td>
                        <td className="py-2 pr-3">
                          <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-[#F3F0E7] text-[#5C5647]">{a.ultimo_passo}</span>
                          {a.motivo && <p className="text-[11px] text-[#B08900] mt-0.5">{a.motivo}</p>}
                        </td>
                        <td className="py-2 text-[#978D78] font-mono text-xs whitespace-nowrap">{a.data || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
